import { comparisonData, platformComparison } from "@/mocks/categories";

export default function ComparisonSection() {
  return (
    <section className="w-full px-4 md:px-8 py-16 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="mb-12">
          <h2 className="text-2xl md:text-4xl font-extrabold text-foreground-950">
            Product Categories &amp; Comparison
          </h2>
          <p className="mt-3 text-base text-foreground-600 max-w-xl">
            Compare sellers, shipping times, and prices across categories in our AllChinaBuy Spreadsheet
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-foreground-900 rounded-lg p-5 md:p-6">
            <h3 className="text-lg font-bold text-background-50 mb-2">
              AllChinaBuy Spreadsheet Comparison
            </h3>
            <p className="text-sm text-foreground-400 mb-5">
              Top-rated sellers in the AllChinaBuy Spreadsheet for 2026
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-foreground-700">
                    <th className="pb-2 font-semibold text-primary-300 whitespace-nowrap pr-3">Seller</th>
                    <th className="pb-2 font-semibold text-primary-300 whitespace-nowrap pr-3">Rating</th>
                    <th className="pb-2 font-semibold text-primary-300 whitespace-nowrap pr-3">Price</th>
                    <th className="pb-2 font-semibold text-primary-300 whitespace-nowrap">Shipping</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row) => (
                    <tr key={row.seller} className="border-b border-foreground-800/50">
                      <td className="py-2.5 text-background-50 whitespace-nowrap pr-3">{row.seller}</td>
                      <td className="py-2.5 text-background-50/80 whitespace-nowrap pr-3 text-xs">{row.rating}</td>
                      <td className="py-2.5 text-background-50/80 whitespace-nowrap pr-3">{row.price}</td>
                      <td className="py-2.5 text-background-50/80 whitespace-nowrap text-xs">{row.shipping}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-5">
              <img
                src="https://readdy.ai/api/search-image?query=Moody%20dark%20tabletop%20product%20arrangement%20with%20premium%20sneakers%20and%20leather%20goods%20on%20charcoal%20surface%2C%20dramatic%20warm%20accent%20lighting%20from%20above%2C%20luxury%20editorial%20photography%2C%20rich%20textures%2C%20no%20text%2C%20no%20branding&width=600&height=400&seq=comp-v2-img&orientation=landscape"
                alt="AllChinaBuy Spreadsheet seller comparison"
                title="Seller Comparison - AllChinaBuy Spreadsheet"
                className="w-full h-32 object-cover object-top rounded-md"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-semibold rounded-full">
                <i className="ri-shield-check-line w-3.5 h-3.5 flex items-center justify-center"></i>
                Verified
              </span>
              <h4 className="mt-3 text-base font-bold text-foreground-900">
                Trusted Seller Ratings
              </h4>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                Every seller in our AllChinaBuy Spreadsheet is rated by real buyers with transparent reviews and verified transaction history.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
              >
                View Sellers <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>

            <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-accent-100 text-accent-800 text-xs font-semibold rounded-full">
                <i className="ri-price-tag-3-line w-3.5 h-3.5 flex items-center justify-center"></i>
                Best Price
              </span>
              <h4 className="mt-3 text-base font-bold text-foreground-900">
                Price Comparison Tool
              </h4>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                Compare prices across multiple sellers for the same product in the AllChinaBuy Spreadsheet to get the best deal every time.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
              >
                Compare Prices <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>

            <div className="sm:col-span-2 bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70">
              <h4 className="text-base font-bold text-foreground-900 mb-4">
                AllChinaBuy vs Other Agents — Platform Comparison
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-background-300/60">
                      <th className="pb-3 font-semibold text-foreground-800 whitespace-nowrap pr-4">Feature</th>
                      <th className="pb-3 font-semibold text-primary-600 whitespace-nowrap pr-4">AllChinaBuy Spreadsheet</th>
                      <th className="pb-3 font-semibold text-foreground-500 whitespace-nowrap">Other Agents</th>
                    </tr>
                  </thead>
                  <tbody>
                    {platformComparison.map((row) => (
                      <tr key={row.feature} className="border-b border-background-200/50">
                        <td className="py-3 text-foreground-800 whitespace-nowrap pr-4">{row.feature}</td>
                        <td className="py-3 text-primary-600 font-medium whitespace-nowrap pr-4">{row.allchinabuy}</td>
                        <td className="py-3 text-foreground-500 whitespace-nowrap">{row.others}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}